/**
 * Comida — DESKTOP.
 *
 * Tres columnas: agregar a la izquierda, el día en el centro, y la tendencia de proteína y
 * calorías a la derecha. Lo que el ancho permite y el celular no: **la tabla del borrador editable
 * con todos los alimentos y sus macros a la vez**, con el JSON del modelo desplegable al lado para
 * ver por qué estimó lo que estimó.
 *
 * Y la tabla de los últimos días, que es donde se ve si el registro es constante — el dato que
 * decide si el promedio significa algo.
 */

import { useRef, type ReactNode } from 'react';
import { formatearNumero, type MomentoDelDia } from '@mg/core';

import { useNutricion, type FilaBorrador } from '../features/useNutricion.ts';
import { Barras } from '../ui/charts.tsx';
import { Aviso, Cifra, Icono, Insignia, Pila, Vacio, estadosDeRatio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

export function NutricionDesktop(): ReactNode {
  const n = useNutricion();

  if (n.datos.cargando) return <Vacio>Cargando…</Vacio>;

  return (
    <>
      <div className="d-rejilla d-rejilla--4">
        {/* El día que se está mirando se elige abajo: estas tarjetas no pueden decir "hoy"
            cuando el usuario está mirando el martes pasado. */}
        <div className="tarjeta">
          <span className="eyebrow">Proteína · {n.etiquetaDia}</span>
          <Cifra valor={n.totales.proteina_g} unidad="g" decimales={0} />
          {n.objetivos.proteina_g ? (
            <div style={{ marginTop: 8 }}>
              <Pila estados={estadosDeRatio(n.totales.proteina_g / n.objetivos.proteina_g, 12)} alto={12} />
              <p className="nota--fina">de {n.objetivos.proteina_g} g — tu objetivo</p>
            </div>
          ) : (
            <PonerObjetivo>sin objetivo puesto. La app no calcula uno.</PonerObjetivo>
          )}
        </div>

        <div className="tarjeta">
          <span className="eyebrow">Calorías · {n.etiquetaDia}</span>
          <Cifra valor={n.totales.kcal} unidad="kcal" decimales={0} />
          <p className="nota--fina">
            {n.totales.items} {n.totales.items === 1 ? 'alimento' : 'alimentos'}
          </p>
        </div>

        <div className="tarjeta">
          <span className="eyebrow">Proteína por kg</span>
          <Cifra valor={n.proteina.gramosPorKg} unidad="g/kg" decimales={2} />
          <p className="nota--fina">
            {n.proteina.pesoKg === null
              ? 'falta una medición de peso'
              : `sobre ${formatearNumero(n.proteina.pesoKg)} kg del ${n.proteina.pesoDe}`}
          </p>
        </div>

        <div className="tarjeta">
          <span className="eyebrow">Promedio 7 días</span>
          <Cifra valor={n.promedio7.proteina_g} unidad="g" decimales={0} />
          <p className="nota--fina" style={{ color: n.promedio7.registroEscaso ? 'var(--ambar)' : undefined }}>
            de {n.promedio7.diasConRegistro} días anotados
          </p>
        </div>
      </div>

      {n.promedio7.registroEscaso && n.promedio7.diasConRegistro > 0 && (
        <Aviso tono="atencion" titulo="El promedio se apoya en pocos días">
          Anotaste {n.promedio7.diasConRegistro} de los últimos {n.promedio7.diasDeVentana} días. Los días
          sin registro no cuentan como cero —eso inventaría un déficit que no existió— pero con pocos
          días el promedio tampoco representa gran cosa.
        </Aviso>
      )}

      <div className="d-rejilla" style={{ gridTemplateColumns: '340px minmax(0, 1fr) 320px', alignItems: 'start' }}>
        <Agregar n={n} />

        <div className="columna" style={{ gap: 'var(--e4)' }}>
          {n.paso === 'revisar' ? (
            <TablaBorrador n={n} />
          ) : (
            <>
              <AvisoBorrado n={n} />
              <TablaDelDia n={n} />
            </>
          )}
        </div>

        <aside className="columna" style={{ gap: 'var(--e4)' }}>
          <section className="tarjeta">
            <span className="eyebrow">Proteína por día</span>
            <Barras
              datos={n.historial.map((d) => ({ etiqueta: d.fecha.slice(8), valor: d.proteina_g }))}
              alto={130}
              objetivo={n.objetivos.proteina_g}
              unidad="g"
            />
            {n.objetivos.proteina_g ? (
              <p className="nota--fina">La línea es tu objetivo de {n.objetivos.proteina_g} g.</p>
            ) : (
              <PonerObjetivo>Sin objetivo no hay línea contra la cual leer estas barras.</PonerObjetivo>
            )}
          </section>

          <section className="tarjeta">
            <span className="eyebrow">Calorías por día</span>
            <Barras
              datos={n.historial.map((d) => ({ etiqueta: d.fecha.slice(8), valor: d.kcal }))}
              alto={130}
              objetivo={n.objetivos.kcal}
              unidad="kcal"
            />
          </section>

          <section className="tarjeta">
            <span className="eyebrow">Constancia del registro</span>
            <p className="nota" style={{ marginTop: 4 }}>
              Anotaste {n.constanciaRegistro.diasConRegistro} de los últimos{' '}
              {n.constanciaRegistro.diasDeVentana} días.
              {n.constanciaRegistro.diasSeguidosSinAnotar > 0 &&
                ` Van ${n.constanciaRegistro.diasSeguidosSinAnotar} días sin anotar.`}
            </p>
            <div style={{ marginTop: 'var(--e2)' }}>
              <Pila
                estados={estadosDeRatio(n.constanciaRegistro.proporcion, 14)}
                alto={12}
                etiqueta="Días anotados de los últimos 14"
              />
            </div>
            <p className="nota--fina" style={{ marginTop: 6 }}>
              Es información sobre el registro, no un reproche.
            </p>
          </section>
        </aside>
      </div>

      {n.historial.length > 0 && (
        <section className="tarjeta" style={{ padding: 0 }}>
          <div style={{ padding: 'var(--e4)' }}>
            <span className="eyebrow">Últimos días con registro</span>
          </div>
          <table className="tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th className="num">Alimentos</th>
                <th className="num">kcal</th>
                <th className="num">Proteína</th>
                <th className="num">Carbos</th>
                <th className="num">Grasa</th>
                <th className="num">Certeza mín.</th>
                {n.objetivos.proteina_g ? <th style={{ width: 140 }}>vs. tu objetivo</th> : null}
              </tr>
            </thead>
            <tbody>
              {[...n.historial].reverse().map((d) => (
                <tr key={d.fecha}>
                  <td className="lectura">{d.fecha}</td>
                  <td className="num">{d.items}</td>
                  <td className="num">{formatearNumero(d.kcal, 0)}</td>
                  <td className="num">{formatearNumero(d.proteina_g, 0)} g</td>
                  <td className="num">{formatearNumero(d.carbohidratos_g, 0)} g</td>
                  <td className="num">{formatearNumero(d.grasa_g, 0)} g</td>
                  <td className="num">
                    {d.confianzaMinima === null ? (
                      <span className="apagado">a mano</span>
                    ) : (
                      <span style={{ color: d.confianzaMinima < n.umbralConfianza ? 'var(--ambar)' : undefined }}>
                        {Math.round(d.confianzaMinima * 100)} %
                      </span>
                    )}
                  </td>
                  {n.objetivos.proteina_g ? (
                    <td>
                      <Pila estados={estadosDeRatio(d.proteina_g / n.objetivos.proteina_g, 10)} alto={10} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * §10 prohíbe prescribir calorías, así que el objetivo lo escribe el usuario. Lo que no puede
 * pasar es nombrar Ajustes sin llevar hasta ahí: un texto que dice dónde se hace algo tiene que
 * ser el botón que lo hace.
 */
function PonerObjetivo({ children }: { children: ReactNode }): ReactNode {
  const { ir } = useRuta();

  return (
    <>
      <p className="nota--fina">{children}</p>
      <button
        type="button"
        className="boton boton--chico"
        style={{ marginTop: 6 }}
        onClick={() => ir('ajustes', 'objetivos')}
      >
        Poner un objetivo
      </button>
    </>
  );
}

/**
 * Momento, hora y día. Va en los dos pasos: confirmar sin ver adónde va es confirmar a ciegas.
 *
 * El momento arranca como sugerencia de la hora y la sigue mientras el usuario no lo toque; apenas
 * elige uno, cambiar la hora ya no se lo mueve. La línea del medio dice en cuál de los dos estados
 * está, porque si no la regla se descubre por accidente.
 */
function Cuando({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  return (
    <>
      <div className="fila" style={{ gap: 6, marginTop: 'var(--e3)', flexWrap: 'wrap' }}>
        {n.momentos.map((m) => (
          <button
            key={m}
            type="button"
            className={n.momento === m ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
            onClick={() => n.setMomento(m as MomentoDelDia)}
            aria-pressed={n.momento === m}
          >
            {n.nombreMomento[m]}
          </button>
        ))}
      </div>

      <p className="nota--fina" style={{ marginTop: 6 }}>
        {n.momentoEsSugerencia
          ? 'Lo sugiere la hora, y la sigue si la cambiás. Elegí otro y queda fijo.'
          : `Lo elegiste vos: ${n.nombreMomento[n.momento]}. Cambiar la hora ya no lo mueve.`}
      </p>

      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
        <input
          className="campo"
          type="time"
          style={{ minHeight: 40, width: 110 }}
          value={n.hora}
          onChange={(e) => n.setHora(e.target.value)}
          aria-label="Hora"
        />
        <input
          className="campo"
          type="date"
          style={{ minHeight: 40 }}
          value={n.dia}
          onChange={(e) => n.setDia(e.target.value)}
          aria-label="Día"
        />
      </div>
    </>
  );
}

/**
 * La vuelta atrás del borrado. Vive fuera de la tabla porque borrar la última fila del día la
 * deja vacía, y con el aviso adentro el "Deshacer" se iría con ella.
 */
function AvisoBorrado({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  if (!n.borrado) return null;

  return (
    <Aviso tono="atencion" titulo="Borrado">
      «{n.borrado.descripcion}» se borró en este dispositivo. Si ya se había subido, vuelve en la
      próxima sincronización.
      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
        <button type="button" className="boton boton--chico" onClick={() => void n.deshacerBorrado()}>
          Deshacer
        </button>
        <button type="button" className="boton boton--chico boton--fantasma" onClick={n.olvidarBorrado}>
          Listo
        </button>
      </div>
    </Aviso>
  );
}

function Agregar({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  const input = useRef<HTMLInputElement>(null);

  return (
    <section className="tarjeta">
      <span className="eyebrow">Agregar una comida</span>

      {n.paso === 'guardado' && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="info">
            Guardado en {n.dondeQueda} ·{' '}
            <span className="lectura">{formatearNumero(n.totalesBorrador.kcal, 0)} kcal</span>,{' '}
            <span className="lectura">{formatearNumero(n.totalesBorrador.proteina_g, 0)} g</span> de proteína.
          </Aviso>
          <button type="button" className="boton" style={{ marginTop: 'var(--e3)', width: '100%' }} onClick={n.reiniciar}>
            Agregar otra
          </button>
        </div>
      )}

      {(n.paso === 'escribir' || n.paso === 'estimando') && (
        <>
          <textarea
            className="campo campo--texto"
            style={{ minHeight: 110, padding: 'var(--e3)', resize: 'vertical', marginTop: 'var(--e2)' }}
            // Sin el "Ej.:" el ejemplo se lee como una comida ya escrita, y el botón apagado al
            // lado parece la app rota.
            placeholder="Ej.: 150 g de pollo a la plancha, una taza de arroz y ensalada con una cucharada de aceite"
            value={n.texto}
            onChange={(e) => n.setTexto(e.target.value)}
            disabled={n.paso === 'estimando'}
            aria-label="Qué comiste"
            onKeyDown={(e) => {
              // Ctrl/Cmd+Enter estima: el mismo gesto que enviar en cualquier campo largo.
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && n.texto.trim()) {
                e.preventDefault();
                void n.estimarTexto();
              }
            }}
          />

          <Cuando n={n} />

          {n.error && (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Aviso tono="importante">
                {n.error.mensaje}
                {n.error.sinRed && (
                  <button
                    type="button"
                    className="boton boton--chico"
                    style={{ marginTop: 'var(--e2)' }}
                    onClick={n.empezarAMano}
                  >
                    A mano
                  </button>
                )}
              </Aviso>
            </div>
          )}

          <button
            type="button"
            className="boton boton--principal"
            style={{ width: '100%', marginTop: 'var(--e3)' }}
            disabled={!n.texto.trim() || n.paso === 'estimando'}
            onClick={() => void n.estimarTexto()}
          >
            {n.paso === 'estimando' ? 'Estimando…' : 'Estimar'}
          </button>
          <p className="nota--fina" style={{ textAlign: 'center', marginTop: 4 }}>
            {n.texto.trim() ? 'o ⌘/Ctrl + Enter' : 'Escribí qué comiste para poder estimar.'}
          </p>

          <input
            ref={input}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void n.estimarFoto(f);
            }}
          />

          <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
            <button type="button" className="boton boton--chico boton--fantasma crece" onClick={() => input.current?.click()}>
              <Icono nombre="camara" tamano={15} />
              Foto
            </button>
            <button type="button" className="boton boton--chico boton--fantasma crece" onClick={n.empezarAMano}>
              A mano
            </button>
          </div>

          <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
            Con la cantidad explícita la estimación es mucho mejor. Sin cantidad, la app asume una
            porción típica y lo aclara en la descripción.
          </p>
        </>
      )}

      {n.paso === 'revisar' && (
        <>
          {n.vistaPrevia && (
            <img
              src={n.vistaPrevia}
              alt="La foto estimada"
              style={{ width: '100%', borderRadius: 'var(--r)', marginTop: 'var(--e2)' }}
            />
          )}
          <p className="nota" style={{ marginTop: 'var(--e3)' }}>
            {n.modelo ? `Estimado por ${n.modelo}.` : 'Cargado a mano.'} Revisá la tabla y confirmá.
          </p>

          <div style={{ marginTop: 'var(--e3)' }}>
            <span className="eyebrow">Dónde va a quedar</span>
            {/* `horaAGuardar` y no `hora`: si el campo de hora quedó vacío, esta línea tiene que
                decir con qué hora se guarda, no un blanco. */}
            <p style={{ marginTop: 4 }}>
              <strong>{n.nombreMomento[n.momento]}</strong> · {n.etiquetaDia}{' '}
              <span className="lectura">{n.horaAGuardar}</span>
            </p>
            <Cuando n={n} />
          </div>

          {n.noEstimables.length > 0 && (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Aviso tono="info" titulo="No lo pudo estimar">
                {n.noEstimables.join(' · ')}
              </Aviso>
            </div>
          )}

          {n.jsonCrudo && (
            <details style={{ marginTop: 'var(--e3)' }}>
              <summary className="nota--fina" style={{ cursor: 'pointer' }}>
                Ver el JSON del modelo
              </summary>
              <pre
                className="lectura"
                style={{
                  fontSize: 11,
                  overflowX: 'auto',
                  background: 'var(--hierro-hundido)',
                  padding: 'var(--e2)',
                  borderRadius: 'var(--r-chico)',
                  marginTop: 6,
                }}
              >
                {JSON.stringify(JSON.parse(n.jsonCrudo), null, 2)}
              </pre>
            </details>
          )}

          <button
            type="button"
            className="boton boton--fantasma boton--chico"
            style={{ width: '100%', marginTop: 'var(--e3)' }}
            onClick={n.reiniciar}
          >
            Descartar
          </button>
        </>
      )}
    </section>
  );
}

function TablaBorrador({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  return (
    <section className="tarjeta" style={{ padding: 0 }}>
      <div className="fila fila--entre" style={{ padding: 'var(--e4)' }}>
        <span className="eyebrow">Revisá antes de guardar</span>
        <span className="nota--fina">las celdas se editan acá mismo</span>
      </div>

      <div className="desborda-x">
        <table className="tabla">
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Qué es</th>
              <th className="num" style={{ width: 90 }}>kcal</th>
              <th className="num" style={{ width: 90 }}>Proteína</th>
              <th className="num" style={{ width: 90 }}>Carbos</th>
              <th className="num" style={{ width: 90 }}>Grasa</th>
              <th className="num" style={{ width: 80 }}>Certeza</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {n.filas.map((f, i) => (
              <FilaBorradorTabla key={i} f={f} i={i} n={n} />
            ))}
            <tr>
              <td>
                <button type="button" className="boton boton--chico boton--fantasma" onClick={n.agregarFila}>
                  <Icono nombre="mas" tamano={14} />
                  Agregar alimento
                </button>
              </td>
              <td className="num lectura">{formatearNumero(n.totalesBorrador.kcal, 0)}</td>
              <td className="num lectura">{formatearNumero(n.totalesBorrador.proteina_g, 0)} g</td>
              <td colSpan={4} />
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ padding: 'var(--e4)' }}>
        {n.errores.length > 0 && (
          <Aviso tono="importante" titulo="Eso no se puede guardar">
            {[...new Set(n.errores.map((h) => h.mensaje))].join(' ')}
          </Aviso>
        )}

        {n.errores.length === 0 && n.aConfirmar.length > 0 && (
          <Aviso tono="atencion">
            {[...new Set(n.aConfirmar.map((h) => h.mensaje))].join(' ')}
            <label className="fila" style={{ marginTop: 8, gap: 8 }}>
              <input type="checkbox" checked={n.confirmado} onChange={(e) => n.setConfirmado(e.target.checked)} />
              <span>Está bien así</span>
            </label>
          </Aviso>
        )}

        {/* El fallo al guardar se pinta acá: si no, el botón vuelve de "Guardando…" y no cambia
            nada, que se lee como que el clic no entró. */}
        {n.error && (
          <div style={{ marginTop: 'var(--e3)' }}>
            <Aviso tono="importante">{n.error.mensaje}</Aviso>
          </div>
        )}

        <button
          type="button"
          className="boton boton--principal"
          style={{ marginTop: 'var(--e3)' }}
          disabled={!n.puedeGuardar || n.guardando}
          onClick={() => void n.confirmar()}
        >
          {n.guardando ? 'Guardando…' : 'Confirmar y guardar'}
        </button>
      </div>
    </section>
  );
}

function FilaBorradorTabla({
  f,
  i,
  n,
}: {
  f: FilaBorrador;
  i: number;
  n: ReturnType<typeof useNutricion>;
}): ReactNode {
  const v = n.validaciones[i];
  const conError = v ? !v.ok : false;
  const celda = { minHeight: 32, textAlign: 'right' as const };

  return (
    <>
      <tr>
        <td>
          <input
            className="campo campo--texto"
            style={{ minHeight: 32 }}
            value={f.descripcion}
            onChange={(e) => n.cambiarFila(i, { descripcion: e.target.value })}
            aria-label={`Descripción del alimento ${i + 1}`}
          />
        </td>
        {(['kcal', 'proteina_g', 'carbohidratos_g', 'grasa_g'] as const).map((clave) => (
          <td key={clave}>
            <input
              className={f.revisar && (clave === 'kcal' || clave === 'proteina_g') ? 'campo campo--revisar' : 'campo'}
              style={celda}
              inputMode="decimal"
              value={f[clave]}
              onChange={(e) => n.cambiarFila(i, { [clave]: e.target.value })}
              aria-label={`${clave} del alimento ${i + 1}`}
            />
          </td>
        ))}
        <td className="num">
          {f.confianza === null ? (
            <span className="apagado">—</span>
          ) : (
            <span style={{ color: f.revisar ? 'var(--ambar)' : undefined }}>{Math.round(f.confianza * 100)} %</span>
          )}
        </td>
        <td>
          {n.filas.length > 1 && (
            <button
              type="button"
              className="boton boton--chico boton--fantasma"
              style={{ minHeight: 30, padding: '0 6px' }}
              onClick={() => n.quitarFila(i)}
              aria-label={`Quitar el alimento ${i + 1}`}
            >
              <Icono nombre="cerrar" tamano={13} />
            </button>
          )}
        </td>
      </tr>
      {conError && (
        <tr>
          <td colSpan={7} style={{ paddingTop: 0 }}>
            <p className="nota--fina" style={{ color: 'var(--ladrillo)' }}>
              {v!.hallazgos.find((h) => h.severidad === 'error')?.mensaje}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

function TablaDelDia({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  if (n.delDia.length === 0) {
    return (
      <section className="tarjeta">
        <span className="eyebrow">{n.dia === n.datos.hoy ? 'Hoy' : n.dia}</span>
        <Vacio>
          No hay nada anotado este día.
          <br />
          Escribí lo que comiste en el panel de la izquierda.
        </Vacio>
      </section>
    );
  }

  return (
    <section className="tarjeta" style={{ padding: 0 }}>
      <div className="fila fila--entre" style={{ padding: 'var(--e4)' }}>
        <span className="eyebrow">{n.dia === n.datos.hoy ? 'Hoy' : n.dia}</span>
        <span className="lectura nota--fina">
          {formatearNumero(n.totales.kcal, 0)} kcal · {formatearNumero(n.totales.proteina_g, 0)} g de proteína
        </span>
      </div>

      <table className="tabla">
        <thead>
          <tr>
            <th>Hora</th>
            <th>Momento</th>
            <th>Qué</th>
            <th className="num">kcal</th>
            <th className="num">Proteína</th>
            <th className="num">Carbos</th>
            <th className="num">Grasa</th>
            <th className="num">Certeza</th>
            <th style={{ width: 44 }} />
          </tr>
        </thead>
        <tbody>
          {n.delDia.map((a) => (
            <tr key={a.id}>
              <td className="lectura apagado">{a.hora}</td>
              <td>
                <Insignia>{n.nombreMomento[a.momento]}</Insignia>
              </td>
              <td>{a.descripcion}</td>
              <td className="num">{formatearNumero(a.kcal, 0)}</td>
              <td className="num">{formatearNumero(a.proteina_g, 1)} g</td>
              <td className="num">{a.carbohidratos_g === null ? '—' : `${formatearNumero(a.carbohidratos_g, 0)} g`}</td>
              <td className="num">{a.grasa_g === null ? '—' : `${formatearNumero(a.grasa_g, 0)} g`}</td>
              <td className="num">
                {a.confianza === null ? (
                  <span className="apagado">a mano</span>
                ) : (
                  <span style={{ color: a.confianza < n.umbralConfianza ? 'var(--ambar)' : undefined }}>
                    {Math.round(a.confianza * 100)} %
                  </span>
                )}
              </td>
              <td>
                <button
                  type="button"
                  className="boton boton--chico boton--fantasma"
                  style={{ minHeight: 30, padding: '0 6px' }}
                  onClick={() => void n.borrarConDeshacer(a)}
                  aria-label={`Borrar ${a.descripcion}`}
                >
                  <Icono nombre="cerrar" tamano={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
